import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { assertDefined } from '../../../../utils/assertDefined';
import { getSdkChainFromApiChainName } from '../getSdkChainFromApiChainName';
import type { NetworkData } from '../networkProvider/networkProvider.types';

/**
 * Retrieves all available network configurations from project settings.
 *
 * This function returns detailed configuration data for all networks enabled
 * in your Dynamic project, including RPC URLs, native currencies, and metadata.
 *
 * If a `transformers.networkData` callback was provided in the client configuration,
 * it will be applied to each network before returning the data. This allows you to
 * customize network properties (such as RPC URLs) in a centralized way.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An array of network configuration data for all enabled networks.
 */
export const getNetworksData = (client = getDefaultClient()): NetworkData[] => {
  const core = getCore(client);
  const { state, transformers } = core;

  const networkDataTransformer =
    transformers?.networkData ?? ((networkData) => networkData);

  const { projectSettings } = state.get();

  assertDefined(projectSettings, 'projectSettings not found');

  const networks = projectSettings.networks;

  if (!networks) {
    return [];
  }

  const networksData = networks
    .map((network) => {
      if (!network.chainName) return [];

      const chain = getSdkChainFromApiChainName(network.chainName);

      if (!network?.networks?.length || !chain) return [];

      return network.networks.map((networkConfiguration) => {
        const networkData: NetworkData = {
          blockExplorerUrls: networkConfiguration.blockExplorerUrls,
          chain,
          cluster: networkConfiguration.cluster,
          displayName:
            networkConfiguration.vanityName || networkConfiguration.name,
          genesisHash: networkConfiguration.genesisHash,
          iconUrl: networkConfiguration.iconUrls[0],
          nativeCurrency: {
            decimals: networkConfiguration.nativeCurrency.decimals,
            iconUrl: networkConfiguration.nativeCurrency.iconUrl,
            name: networkConfiguration.nativeCurrency.name,
            symbol: networkConfiguration.nativeCurrency.symbol,
          },
          networkId: networkConfiguration.networkId,
          rpcUrls: {
            http: [
              ...(networkConfiguration.privateCustomerRpcUrls ?? []),
              ...(networkConfiguration.rpcUrls ?? []),
            ],
          },
          testnet: networkConfiguration.isTestnet ?? false,
        };

        return networkDataTransformer(networkData);
      });
    })
    .flat();

  return networksData;
};
